<template>
    <div class="container admin-table-page">
        <!-- ==================== TÍTULO ==================== -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="d-flex align-items-center">
                    <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
                        <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" class="back-icon" />
                    </b-button>
                    <h2 class="param-title">{{ tituloConsolidado || labels.title }}</h2>
                </div>
            </div>
        </div>

        <!-- ==================== FILTROS ==================== -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100" style="flex-wrap: wrap; gap: 8px;">

                <!-- Filtros principales -->
                <div class="filters-container d-flex align-items-center" style="flex-wrap: wrap; gap: 8px;">

                    <!-- Vigencia -->
                    <b-form-select
                        v-model="dialogFechas"
                        @change="onFiltroChange"
                        class="filter-select"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectValidity }}
                        </b-form-select-option>
                        <option
                            v-for="fecha in fechas"
                            :key="fecha.fecha"
                            :value="fecha.fecha"
                        >
                            {{ fecha?.fecha }}
                        </option>
                    </b-form-select>

                    <!-- Grupo -->
                    <b-form-select
                        v-model="grupoId"
                        @change="filtraPorGrupo()"
                        class="filter-select"
                    >
                        <option value="all">
                            {{ labels.allGroups }}
                        </option>
                        <option
                            v-for="grupo in listGrupos"
                            :key="'grupo-' + grupo.id"
                            :value="grupo.id"
                        >
                            {{ grupo?.nombre }}
                        </option>
                    </b-form-select>

                </div>

                <!-- Botón Consultar -->
                <div class="filters-container d-flex align-items-center">
                    <b-button
                        class="consult-btn"
                        @click="f_consultar"
                        :disabled="!dialogFechas || isLoading"
                    >
                        <font-awesome-icon :icon="['fas', 'search']" class="mr-1" />
                        {{ labels.consultar }}
                    </b-button>
                </div>

                <!-- Nombres de compañías a la derecha -->
                <div v-if="fgl_detalle && nombreCompaniaOrigenDetalle && nombreCompaniaDestinoDetalle" 
                    class="d-flex align-items-center" style="gap: 8px; flex-shrink: 0; padding-left: 20px; border-left: 1px solid #dee2e6; margin-left: auto;">
                    <span style="font-size: 13px; font-weight: 500;">
                        <font-awesome-icon :icon="['fas', 'building']" class="mr-1" />
                        {{ nombreCompaniaOrigenDetalle }}
                    </span>
                    <span style="font-size: 13px;">
                        <font-awesome-icon :icon="['fas', 'arrow-right']" />
                    </span>
                    <span style="font-size: 13px; font-weight: 500;">
                        <font-awesome-icon :icon="['fas', 'building']" class="mr-1" />
                        {{ nombreCompaniaDestinoDetalle }}
                    </span>
                </div>

            </div>
        </div>

        <!-- ========================================================= -->
        <!-- ==================== CONTENIDO PRINCIPAL ================= -->
        <!-- ========================================================= -->
        <div class="admin-table-block mt-4">

            <!-- ===== ESTADO VACÍO O CARGANDO (Siempre que no haya datos) ===== -->
            <div v-if="!fgl_detalle && (filteredIntercos.length === 0 || isLoading)">
                <CorporateDataTable
                    :items="[]"
                    :fields="[]"
                    :labels="tableLabels"
                    :loading="isLoading"
                    :default-per-page="10"
                    :page-options="[10]"
                    :narrow="false"
                    :table-min-height="400"
                    :table-max-height="500"
                    :show-search="false"
                    :allow-column-toggle="false"
                    :enable-sorting="false"
                    :show-pagination="false"
                    :show-rows-per-page="false"
                >
                    <template #empty-state>
                        <div class="text-center py-5">
                            <font-awesome-icon
                                v-if="!isLoading"
                                :icon="['fas', 'search']"
                                size="3x"
                                class="text-muted mb-3"
                                style="opacity: 0.3;"
                            />
                            <p class="h4 text-muted" v-if="!isLoading">
                                {{ labels.selectCriteria }}
                            </p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- ==================== TABLA PRINCIPAL ==================== -->
            <div v-if="!fgl_detalle && !isLoading && filteredIntercos.length > 0" class="intercos-table-wrapper">
                <CorporateDataTable
                    :items="itemsTabla"
                    :fields="allFields"
                    :labels="tableLabels"
                    :loading="false"
                    :default-per-page="filteredIntercos.length || 10"
                    :page-options="[filteredIntercos.length || 10]"
                    :narrow="false"
                    :table-min-height="500"
                    :table-max-height="500"
                    :show-search="true"
                    :allow-column-toggle="true"
                    :enable-sorting="true"
                    :filter-defs="tableFilterDefs"
                    :show-pagination="false"
                    :show-rows-per-page="false"
                    @row-clicked="onRowSelected"
                >
                    <template #toolbar-actions>
                        <div class="niif-toolbar-left d-flex align-items-center" style="gap: 8px; flex-wrap: wrap;">
                            <!-- Info de última generación -->
                            <span v-if="dialogFechas && fgl_generado" class="generation-info">
                                <span v-if="cargandoUltimaGeneracion">
                                    <b-spinner size="sm" variant="primary" class="mr-1"></b-spinner>
                                    {{ labels.checkingGeneration }}
                                </span>
                                <span v-else-if="ultimaGeneracionIntercos" class="text-muted small">
                                    <font-awesome-icon :icon="['fas', 'clock']" class="mr-1" />
                                    {{ labels.lastGeneration }}: {{ formatFechaGeneracion(ultimaGeneracionIntercos) }}
                                </span>
                                <span v-else class="text-muted small">
                                    <font-awesome-icon :icon="['fas', 'info-circle']" class="mr-1" />
                                    {{ labels.noGeneration }}
                                </span>
                            </span>
                        </div>
                        <div class="niif-toolbar-right d-flex align-items-center" style="gap: 4px; flex-wrap: wrap;">

                            <!-- Botón Generar -->
                            <b-button
                                size="sm"
                                class="table-toolbar-btn"
                                @click="f_generar"
                                :disabled="isVigenciaBloqueada || isGenerating || secuenciaBloqueada"
                            >
                                <font-awesome-icon :icon="['fas', 'sync']" class="mr-1" />
                                {{ labels.generar }}
                            </b-button>

                            <!-- Botón Exportar con Dropdown -->
                            <b-dropdown
                                v-if="!selectionMode"
                                variant="outline-secondary"
                                size="sm"
                                class="table-toolbar-dropdown"
                                toggle-class="table-toolbar-btn table-toolbar-dropdown-btn"
                            >
                                <template #button-content>
                                    <font-awesome-icon :icon="['fas', 'file-excel']" class="mr-1" />
                                    {{ labels.exportar }}
                                </template>
                                <b-dropdown-item @click="excel(1)">
                                    <font-awesome-icon :icon="['fas', 'file-excel']" class="mr-2" />
                                    {{ labels.excelGeneral }}
                                </b-dropdown-item>
                                <b-dropdown-item @click="toggleSelectionMode">
                                    <font-awesome-icon :icon="['fas', 'check-double']" class="mr-2" />
                                    {{ labels.selectRows }}
                                </b-dropdown-item>
                            </b-dropdown>

                            <!-- Botones de Excel adicionales -->
                            <b-button
                                v-if="!selectionMode"
                                size="sm"
                                class="table-toolbar-btn"
                                @click="verHuerfanos"
                            >
                                <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mr-1" />
                                {{ labels.unmatched }}
                            </b-button>
                            <b-button
                                v-if="!selectionMode"
                                size="sm"
                                class="table-toolbar-btn"
                                @click="verDiferenciasSecuencia"
                            >
                                <font-awesome-icon :icon="['fas', 'arrows-alt-h']" class="mr-1" />
                                {{ labels.seqDifferences }}
                            </b-button>

                            <!-- Botones de selección -->
                            <template v-else>
                                <b-button
                                    size="sm"
                                    class="table-toolbar-btn"
                                    @click="excelSelection"
                                    :disabled="selectedRows.length === 0"
                                >
                                    <font-awesome-icon :icon="['fas', 'download']" class="mr-1" />
                                    {{ labels.download }} ({{ selectedRows.length }})
                                </b-button>
                                <b-button
                                    size="sm"
                                    class="table-toolbar-btn"
                                    @click="toggleSelectionMode"
                                    style="background-color: #dc3545 !important; border-color: #dc3545 !important;"
                                >
                                    <font-awesome-icon :icon="['fas', 'times']" class="mr-1" />
                                    {{ labels.cancelSelection }}
                                </b-button>
                            </template>
                        </div>
                    </template>

                    <template #cell(identificacion_origen)="data">
                        <div class="text-left">{{ data.item?.identificacion_origen }}</div>
                    </template>

                    <template #cell(compania_origen)="data">
                        <div class="text-left">{{ f_compania(data.item?.identificacion_origen) }}</div>
                    </template>

                    <template #cell(saldo_origen)="data">
                        <div class="text-right font-monospace">{{ formato(data.item?.saldo_origen) }}</div>
                    </template>

                    <template #cell(identificacion_destino)="data">
                        <div class="text-left">{{ data.item?.identificacion_destino }}</div>
                    </template>

                    <template #cell(compania_destino)="data">
                        <div class="text-left">{{ f_compania(data.item?.identificacion_destino) }}</div>
                    </template>

                    <template #cell(saldo_destino)="data">
                        <div class="text-right font-monospace">{{ formato(data.item?.saldo_destino) }}</div>
                    </template>

                    <template #cell(diferencia)="data">
                        <div class="text-right font-monospace">{{ formato(f_calcularDiferencia(data.item?.saldo_origen, data.item?.saldo_destino)) }}</div>
                    </template>

                    <template #cell(secuencia)="data">
                        <div class="text-left">{{ data.item?.secuencias || '—' }}</div>
                    </template>

                    <!-- Columna Ver -->
                    <template #cell(accionVer)="data">
                        <b-button
                            style="background-color: #1f4f83; width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border: none; border-radius: 4px;"
                            class="table-action-btn"
                            @click.stop="f_generar_detalle(data.item.identificacion_origen, data.item.identificacion_destino)"
                            :title="labels.viewDetails"
                        >
                            <font-awesome-icon :icon="['fas', 'eye']" size="xs" style="color: white;" />
                        </b-button>
                    </template>

                    <template #empty-state>
                        <div class="text-center py-5">
                            <font-awesome-icon :icon="['fas', 'file-alt']" size="4x" class="text-muted mb-3" style="opacity: 0.3;" />
                            <p class="h4 text-muted">{{ labels.empty }}</p>
                            <b-button
                                v-if="!isVigenciaBloqueada && !secuenciaBloqueada"
                                @click="f_generar"
                                variant="primary"
                                class="mt-3"
                            >
                                <font-awesome-icon :icon="['fas', 'sync']" class="mr-1" />
                                {{ labels.generar }}
                            </b-button>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- ==================== VISTA DE DETALLE ==================== -->
            <div v-if="fgl_detalle" class="detail-view-container">
                <!-- TABLA DE DETALLE CON SPINNER INCORPORADO -->
                <CorporateDataTable
                    :items="isLoadingDetalle ? [] : intercos_detalle"
                    :fields="isLoadingDetalle ? [] : detailFields"
                    :labels="tableLabels"
                    :loading="isLoadingDetalle"
                    :default-per-page="intercos_detalle.length || 10"
                    :page-options="[intercos_detalle.length || 10]"
                    :narrow="false"
                    :table-min-height="400"
                    :table-max-height="500"
                    :show-search="!isLoadingDetalle"
                    :allow-column-toggle="!isLoadingDetalle"
                    :enable-sorting="!isLoadingDetalle"
                    :filter-defs="isLoadingDetalle ? [] : detailFilterDefs"
                    :show-pagination="false"
                    :show-rows-per-page="false"
                    :show-back-button="true"
                    :back-button-text="labels.back"
                    @back="cerrarDetalle"
                >
                    <template #toolbar-actions>
                        <div class="niif-toolbar-left d-flex align-items-center">
                            <!-- ESPACIO VACÍO - SIN TÍTULO -->
                        </div>
                        <div class="niif-toolbar-right d-flex align-items-center" style="gap: 8px;">
                            <b-button
                                size="sm"
                                class="table-toolbar-btn"
                                @click="excel(2)"
                                :disabled="isLoadingDetalle"
                            >
                                <font-awesome-icon :icon="['fas', 'file-excel']" class="mr-1" />
                                {{ labels.exportExcel }}
                            </b-button>
                            <b-button
                                size="sm"
                                class="table-toolbar-btn"
                                @click="fgl_diferencia = !fgl_diferencia"
                                :disabled="isLoadingDetalle"
                                :style="fgl_diferencia ? 'background-color: #1f4f83 !important; border-color: #1f4f83 !important; color: white !important;' : 'background-color: #6c757d !important; border-color: #6c757d !important; color: white !important;'"
                            >
                                <font-awesome-icon :icon="['fas', 'arrows-alt-h']" class="mr-1" />
                                {{ labels.diferencia }}
                            </b-button>
                        </div>
                    </template>

                    <template #cell(identificacion_origen)="data">
                        <div class="text-left">{{ data.item?.identificacion_origen }}</div>
                    </template>

                    <template #cell(cuenta_origen)="data">
                        <div class="text-left">{{ data.item?.cuenta_origen }}</div>
                    </template>

                    <template #cell(descripcion_origen)="data">
                        <div class="text-left">{{ data.item?.descripcion_origen }}</div>
                    </template>

                    <template #cell(saldo_origen)="data">
                        <div class="text-right font-monospace">{{ formato(data.item?.saldo_origen) }}</div>
                    </template>

                    <template #cell(identificacion_destino)="data">
                        <div class="text-left">{{ data.item?.identificacion_destino }}</div>
                    </template>

                    <template #cell(cuenta_destino)="data">
                        <div class="text-left">{{ data.item?.cuenta_destino }}</div>
                    </template>

                    <template #cell(descripcion_destino)="data">
                        <div class="text-left">{{ data.item?.descripcion_destino }}</div>
                    </template>

                    <template #cell(saldo_destino)="data">
                        <div class="text-right font-monospace">{{ formato(data.item?.saldo_destino) }}</div>
                    </template>

                    <template #cell(diferencia)="data">
                        <div class="text-right font-monospace" v-if="fgl_diferencia">{{ formato(data.item?.diferencia) }}</div>
                    </template>

                    <template #empty-state>
                        <div class="text-center py-5">
                            <font-awesome-icon :icon="['fas', 'file-alt']" size="4x" class="text-muted mb-3" style="opacity: 0.3;" />
                            <p class="h4 text-muted">{{ labels.noDetailData }}</p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

        </div>

        <!-- ==================== MODAL DE GENERACIÓN ==================== -->
        <b-modal v-model="showGeneratingModal" title="Generando Intercompañías" hide-footer centered
            no-close-on-backdrop no-close-on-esc>
            <div class="text-center">
                <b-spinner variant="primary" style="width: 3rem; height: 3rem;"></b-spinner>
                <h5 class="mt-3"><b><i>{{ generatingMessage }}</i></b></h5>
                <p class="mt-2">{{ generatingSubMessage }}</p>
                <b-progress v-if="generatingProgress >= 0" class="mt-3">
                    <b-progress-bar :value="generatingProgress" :label="`${generatingProgress}%`" variant="primary"
                        striped animated>
                    </b-progress-bar>
                </b-progress>
            </div>
        </b-modal>

    </div>
</template>

<script>
import Balance from "@/services/BalanceNiif"
import * as XLSX from "exceljs"
import Grupo from "@/services/Grupo"
import CorporateDataTable from '../utils/CorporateDataTable.vue'

export default {
    name: "Intercos",
    components: {
        CorporateDataTable
    },
    props: {
        // NUEVA PROP: bloqueo por secuencia >= 500
        secuenciaBloqueada: { type: Boolean, default: false }
    },
    data: () => ({
        // ==================== VARIABLES DE ESTADO ====================
        fgl_detalle: false,
        fgl_diferencia: false,
        muestra: false,
        isEmpty: true,
        isLoading: false,
        isLoadingDetalle: false,
        showDialog: false,
        showDialogEdit: false,
        dialogItem: {},
        intercos: [],
        filteredIntercos: [],
        intercos_detalle: [],
        vigencia: '',
        opcion: '',
        fgl_generado: false,
        fgl_reporte: 0,
        fgl_excel: true,
        titulo: 1,
        titulo_cuenta: '',
        selectedRows: [],
        selectionMode: false,

        // ==================== NOMBRES DE COMPAÑÍAS PARA DETALLE ====================
        nombreCompaniaOrigenDetalle: '',
        nombreCompaniaDestinoDetalle: '',

        // ==================== MODAL DE GENERACIÓN ====================
        showGeneratingModal: false,
        generatingMessage: "",
        generatingSubMessage: "",
        generatingProgress: -1,
        isGenerating: false,
        ultimaGeneracionIntercos: null,
        cargandoUltimaGeneracion: false,

        // ==================== FILTROS ====================
        dialogFechas: '',
        grupoId: 'all',
        dialogReportes: 0,

        // ==================== LISTAS ====================
        companias: [],
        fechas: [],
        reportes: [],
        listGrupos: [],

        // ==================== OTROS ====================
        tituloConsolidado: "",
    }),
    computed: {
        isVigenciaBloqueada() {
            return this.dialogFechas ? !this.$store.getters.isPeriodoEnabled(this.dialogFechas) : false
        },
        labels() {
            const es = this.$store.state._language == "es-ES"
            return {
                title: es ? "Saldos Intercompañías" : "Intercompany Balances",
                selectValidity: es ? "Seleccione Vigencia" : "Select Validity",
                allGroups: es ? "Todos los grupos" : "All groups",
                consultar: es ? "Consultar" : "Consult",
                generar: es ? "Generar" : "Generate",
                back: es ? "Volver" : "Back",
                diferencia: es ? "Diferencia" : "Difference",
                identificacion_origen: es ? "NIT Origen" : "Origin Tax ID",
                compania_origen: es ? "Compañía Origen" : "Origin Company",
                saldo_origen: es ? "Saldo Origen" : "Origin Balance",
                identificacion_destino: es ? "NIT Destino" : "Destination Tax ID",
                compania_destino: es ? "Compañía Destino" : "Destination Company",
                saldo_destino: es ? "Saldo Destino" : "Destination Balance",
                secuencia: es ? "Secuencia" : "Sequence",
                cuenta_origen: es ? "Cuenta Origen" : "Origin Account",
                descripcion_origen: es ? "Descripción Origen" : "Origin Description",
                cuenta_destino: es ? "Cuenta Destino" : "Destination Account",
                descripcion_destino: es ? "Descripción Destino" : "Destination Description",
                exportar: es ? "Exportar" : "Export",
                excelGeneral: es ? "Excel General" : "General Excel",
                selectRows: es ? "Seleccionar Filas" : "Select Rows",
                unmatched: es ? "Sin Conciliar" : "Unmatched",
                seqDifferences: es ? "Dif. Secuencias" : "Seq. Differences",
                download: es ? "Descargar" : "Download",
                cancelSelection: es ? "Cancelar Selección" : "Cancel Selection",
                lastGeneration: es ? "Última generación" : "Last generation",
                checkingGeneration: es ? "Consultando última generación..." : "Checking last generation...",
                noGeneration: es ? "No hay generaciones registradas" : "No generations registered",
                noDetailData: es ? "No hay datos de detalle disponibles" : "No detail data available",
                empty: es ? "No hay datos disponibles" : "No data available",
                selectCriteria: es ? "Seleccione una vigencia y presione Consultar" : "Select a validity and press Consult",
                loading: es ? "Cargando información..." : "Loading information...",
                exportExcel: es ? "Exportar Excel" : "Export Excel",
                noData: es ? "No hay datos disponibles" : "No data available",
                noMatches: es ? "No se encontraron resultados" : "No results found",
                searchPlaceholder: es ? "Buscar en la tabla..." : "Search in table...",
                rowsPerPage: es ? "Filas por página:" : "Rows per page:",
                noSort: es ? "Sin orden" : "No sorting",
                sortAsc: es ? "A-Z / Menor a mayor" : "A-Z / Low to high",
                sortDesc: es ? "Z-A / Mayor a menor" : "Z-A / High to low",
                columns: es ? "Columnas" : "Columns",
                showAllColumns: es ? "Mostrar todas" : "Show all",
                filters: es ? "Filtros" : "Filters",
                clearFilters: es ? "Limpiar filtros" : "Clear filters",
                ver: es ? "Ver" : "View",
                viewDetails: es ? "Ver detalles" : "View details",
            }
        },
        tableLabels() {
            return {
                noRow: this.labels.noData,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        },
        allFields() {
            return [
                { key: 'identificacion_origen', label: this.labels.identificacion_origen, sortable: true, thClass: 'text-center', tdClass: 'text-left align-middle', minWidth: '120px' },
                { key: 'compania_origen', label: this.labels.compania_origen, sortable: true, thClass: 'text-center', tdClass: 'text-left align-middle', minWidth: '180px' },
                { key: 'saldo_origen', label: this.labels.saldo_origen, sortable: true, thClass: 'text-center', tdClass: 'text-right align-middle', minWidth: '120px' },
                { key: 'identificacion_destino', label: this.labels.identificacion_destino, sortable: true, thClass: 'text-center', tdClass: 'text-left align-middle', minWidth: '120px' },
                { key: 'compania_destino', label: this.labels.compania_destino, sortable: true, thClass: 'text-center', tdClass: 'text-left align-middle', minWidth: '180px' },
                { key: 'saldo_destino', label: this.labels.saldo_destino, sortable: true, thClass: 'text-center', tdClass: 'text-right align-middle', minWidth: '120px' },
                { key: 'diferencia', label: this.labels.diferencia, sortable: true, thClass: 'text-center', tdClass: 'text-right align-middle', minWidth: '120px' },
                { key: 'secuencia', label: this.labels.secuencia, sortable: true, thClass: 'text-center', tdClass: 'text-left align-middle', minWidth: '100px' },
                { key: 'accionVer', label: this.labels.ver, thClass: 'text-center', tdClass: 'text-center', sortable: false, minWidth: '50px' }
            ]
        },
        itemsTabla() {
            return this.filteredIntercos.map(item => ({
                ...item,
                _rowVariant: this.selectionMode && this.isSelected(item) ? 'selected' : null
            }))
        },
        tableFilterDefs() {
            const es = this.$store.state._language == "es-ES"
            return [
                { key: 'identificacion_origen', type: 'text', placeholder: es ? 'Filtrar NIT origen...' : 'Filter origin tax ID...' },
                { key: 'identificacion_destino', type: 'text', placeholder: es ? 'Filtrar NIT destino...' : 'Filter destination tax ID...' },
                { key: 'secuencia', type: 'text', placeholder: es ? 'Filtrar secuencia...' : 'Filter sequence...' }
            ]
        },
        detailFields() {
            const fields = [
                { key: 'identificacion_origen', label: this.labels.identificacion_origen, sortable: true, thClass: 'text-center', tdClass: 'text-left align-middle', minWidth: '100px' },
                { key: 'cuenta_origen', label: this.labels.cuenta_origen, sortable: true, thClass: 'text-center', tdClass: 'text-left align-middle', minWidth: '120px' },
                { key: 'descripcion_origen', label: this.labels.descripcion_origen, sortable: true, thClass: 'text-center', tdClass: 'text-left align-middle', minWidth: '200px' },
                { key: 'saldo_origen', label: this.labels.saldo_origen, sortable: true, thClass: 'text-center', tdClass: 'text-right align-middle', minWidth: '120px' },
                { key: 'identificacion_destino', label: this.labels.identificacion_destino, sortable: true, thClass: 'text-center', tdClass: 'text-left align-middle', minWidth: '100px' },
                { key: 'cuenta_destino', label: this.labels.cuenta_destino, sortable: true, thClass: 'text-center', tdClass: 'text-left align-middle', minWidth: '120px' },
                { key: 'descripcion_destino', label: this.labels.descripcion_destino, sortable: true, thClass: 'text-center', tdClass: 'text-left align-middle', minWidth: '200px' },
                { key: 'saldo_destino', label: this.labels.saldo_destino, sortable: true, thClass: 'text-center', tdClass: 'text-right align-middle', minWidth: '120px' }
            ]
            if (this.fgl_diferencia) {
                fields.push({ key: 'diferencia', label: this.labels.diferencia, sortable: true, thClass: 'text-center', tdClass: 'text-right align-middle', minWidth: '120px' })
            }
            return fields
        },
        detailFilterDefs() {
            const es = this.$store.state._language == "es-ES"
            return [
                { key: 'identificacion_origen', type: 'text', placeholder: es ? 'Filtrar NIT origen...' : 'Filter origin tax ID...' },
                { key: 'cuenta_origen', type: 'text', placeholder: es ? 'Filtrar cuenta origen...' : 'Filter origin account...' },
                { key: 'identificacion_destino', type: 'text', placeholder: es ? 'Filtrar NIT destino...' : 'Filter destination tax ID...' },
                { key: 'cuenta_destino', type: 'text', placeholder: es ? 'Filtrar cuenta destino...' : 'Filter destination account...' }
            ]
        },
        hasSelectedRows() {
            return this.selectedRows && this.selectedRows.length > 0
        }
    },
    watch: {
        filteredIntercos(newVal, oldVal) {
            if (this.selectedRows.length > 0 && newVal !== oldVal) {
                this.selectedRows = this.selectedRows.filter(selected =>
                    newVal.some(item =>
                        item.identificacion_origen === selected.identificacion_origen &&
                        item.identificacion_destino === selected.identificacion_destino
                    )
                )
                if (this.selectedRows.length === 0) {
                    this.$toast.info(this.$store.state._language == "es-ES" ? "Las selecciones anteriores ya no están disponibles con el filtro actual" : "Previous selections are no longer available with the current filter")
                }
            }
        },
        grupoId() {
            if (this.intercos.length > 0) {
                this.filtraPorGrupo()
            }
        },
        dialogFechas(newVal) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error(this.$store.state._language == "es-ES" ? "La vigencia seleccionada está deshabilitada para generar el interco" : "The selected validity is disabled for generating intercompany")
            }
            this.cargarUltimaGeneracion(newVal)
        }
    },
    methods: {
        // ==================== MÉTODOS DE FILTROS ====================
        onFiltroChange() {
            this.isEmpty = true
            this.fgl_generado = false
            this.intercos = []
            this.filteredIntercos = []
            this.selectedRows = []
            this.selectionMode = false
            this.fgl_detalle = false
            this.nombreCompaniaOrigenDetalle = ''
            this.nombreCompaniaDestinoDetalle = ''
        },

        filtraPorGrupo() {
            if (this.grupoId && this.grupoId !== 'all' && this.intercos.length > 0) {
                const grupoIdNum = this.grupoId.toString()
                this.filteredIntercos = this.intercos.filter(interco => {
                    const companiaOrigen = this.companias.find(c => c.identificacion === interco.identificacion_origen)
                    const companiaDestino = this.companias.find(c => c.identificacion === interco.identificacion_destino)
                    if (!companiaOrigen || !companiaDestino) return false

                    const gruposOrigen = companiaOrigen.id_grupos ?
                        companiaOrigen.id_grupos.split(',').map(id => id.trim()) : []
                    const gruposDestino = companiaDestino.id_grupos ?
                        companiaDestino.id_grupos.split(',').map(id => id.trim()) : []

                    const origenEnGrupo = gruposOrigen.includes(grupoIdNum)
                    const destinoEnGrupo = gruposDestino.includes(grupoIdNum)

                    return origenEnGrupo && destinoEnGrupo
                })
            } else {
                this.filteredIntercos = [...this.intercos]
            }

            this.isEmpty = this.filteredIntercos.length === 0
        },

        cerrarDetalle() {
            this.fgl_detalle = false
            this.fgl_diferencia = false
            this.intercos_detalle = []
            this.nombreCompaniaOrigenDetalle = ''
            this.nombreCompaniaDestinoDetalle = ''
        },

        // ==================== MÉTODOS DE GENERACIÓN ====================
        formatFechaGeneracion(fecha) {
            if (!fecha) return ""
            if (typeof fecha === "string") {
                const fechaSimple = fecha.substring(0, 10)
                if (/^\d{4}-\d{2}-\d{2}$/.test(fechaSimple)) {
                    const partes = fechaSimple.split("-")
                    return `${partes[2]}/${partes[1]}/${partes[0]}`
                }
            }
            const dateObj = new Date(fecha)
            if (!isNaN(dateObj.getTime())) {
                const dia = String(dateObj.getDate()).padStart(2, "0")
                const mes = String(dateObj.getMonth() + 1).padStart(2, "0")
                const anio = dateObj.getFullYear()
                return `${dia}/${mes}/${anio}`
            }
            return fecha
        },

        cargarUltimaGeneracion(vigencia) {
            if (!vigencia) {
                this.ultimaGeneracionIntercos = null
                this.cargandoUltimaGeneracion = false
                return
            }
            this.cargandoUltimaGeneracion = true
            Balance.getIntercosUltimaGeneracion(vigencia)
                .then((result) => {
                    this.ultimaGeneracionIntercos = result.data.ultima_generacion || null
                })
                .catch((error) => {
                    console.error("Error al consultar última generación de intercos:", error)
                    this.ultimaGeneracionIntercos = null
                })
                .finally(() => {
                    this.cargandoUltimaGeneracion = false
                })
        },

        closeGenerationModal() {
            this.showGeneratingModal = false
            this.isGenerating = false
            this.generatingProgress = 0
            this.generatingMessage = ''
            this.generatingSubMessage = ''
        },

        forceCloseGenerationModal(message) {
            this.generatingMessage = this.$store.state._language == "es-ES" ? "Proceso interrumpido" : "Process interrupted"
            this.generatingSubMessage = message || (this.$store.state._language == "es-ES" ? "El proceso fue detenido por seguridad" : "The process was stopped for safety")
            this.generatingProgress = 0
            setTimeout(() => {
                this.closeGenerationModal()
                this.$toast.warning(this.$store.state._language == "es-ES" ? "El proceso de generación fue interrumpido por tiempo de espera" : "The generation process was interrupted due to timeout")
            }, 2000)
        },

        f_generar() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error(this.$store.state._language == "es-ES" ? "La vigencia seleccionada está deshabilitada para generar" : "The selected validity is disabled for generating")
                return
            }
            // NUEVO: bloqueo por secuencia >= 500
            if (this.secuenciaBloqueada) {
                this.$toast.error(this.$store.state._language == "es-ES" ? "Esta secuencia (500+) está bloqueada para generar." : "This sequence (500+) is locked for generating.")
                return
            }
            if (this.isGenerating) {
                this.$toast.info(this.$store.state._language == "es-ES" ? "Ya se está generando la información. Por favor espere..." : "Information is already being generated. Please wait...")
                return
            }

            this.showGeneratingModal = true
            this.isGenerating = true
            this.generatingMessage = this.$store.state._language == "es-ES" ? "Generando información de Intercompañías" : "Generating Intercompany information"
            this.generatingSubMessage = this.$store.state._language == "es-ES" ? "Iniciando proceso de generación..." : "Starting generation process..."
            this.generatingProgress = 0

            const safetyTimeout = setTimeout(() => {
                if (this.isGenerating) {
                    console.error("Timeout de seguridad activado - proceso colgado")
                    this.forceCloseGenerationModal(this.$store.state._language == "es-ES" ? "Tiempo de espera agotado. El proceso está tomando más tiempo de lo esperado." : "Timeout. The process is taking longer than expected.")
                }
            }, 600000)

            let progressInterval
            progressInterval = setInterval(() => {
                if (this.generatingProgress < 85) {
                    this.generatingProgress += 3
                    if (this.generatingProgress < 20) {
                        this.generatingSubMessage = this.$store.state._language == "es-ES" ? `Preparando datos iniciales... ${this.generatingProgress}%` : `Preparing initial data... ${this.generatingProgress}%`
                    } else if (this.generatingProgress < 50) {
                        this.generatingSubMessage = this.$store.state._language == "es-ES" ? `Procesando intercos... ${this.generatingProgress}%` : `Processing intercos... ${this.generatingProgress}%`
                    } else if (this.generatingProgress < 80) {
                        this.generatingSubMessage = this.$store.state._language == "es-ES" ? `Consolidando información... ${this.generatingProgress}%` : `Consolidating information... ${this.generatingProgress}%`
                    } else {
                        this.generatingSubMessage = this.$store.state._language == "es-ES" ? `Finalizando proceso... ${this.generatingProgress}%` : `Finishing process... ${this.generatingProgress}%`
                    }
                }
            }, 5000)

            Balance.genereInterCompanias(this.vigencia, this.$store.state.userId)
                .then((response) => {
                    clearTimeout(safetyTimeout)
                    clearInterval(progressInterval)

                    if (!response) {
                        throw new Error("Respuesta vacía del servidor")
                    }

                    this.generatingProgress = 100
                    this.generatingMessage = this.$store.state._language == "es-ES" ? "¡Generación Completada!" : "Generation Completed!"
                    this.generatingSubMessage = this.$store.state._language == "es-ES" ? "La información de Intercompañías se ha generado exitosamente." : "Intercompany information has been generated successfully."

                    setTimeout(() => {
                        this.closeGenerationModal()
                        this.$toast.success(this.$store.state._language == "es-ES" ? "Información Intercompañías Generada" : "Intercompany Information Generated")
                        this.f_consultar()
                        this.cargarUltimaGeneracion(this.vigencia)
                    }, 1500)
                })
                .catch((error) => {
                    clearTimeout(safetyTimeout)
                    clearInterval(progressInterval)
                    console.error("Error detallado en f_generar:", error)

                    let errorMessage = this.$store.state._language == "es-ES" ? "Ha ocurrido un problema al generar la información." : "An error occurred while generating information."
                    if (error.response && error.response.status === 500) {
                        errorMessage = this.$store.state._language == "es-ES" ? "Error interno del servidor. Contacte al administrador." : "Internal server error. Contact the administrator."
                    } else if (error.message && error.message.includes("timeout")) {
                        errorMessage = this.$store.state._language == "es-ES" ? "El tiempo de espera se agotó. El servidor está tardando demasiado." : "Timeout. The server is taking too long."
                    } else if (error.message && error.message.includes("Network Error")) {
                        errorMessage = this.$store.state._language == "es-ES" ? "Error de conexión. Verifique su red e intente nuevamente." : "Network error. Check your network and try again."
                    }

                    this.generatingMessage = this.$store.state._language == "es-ES" ? "Error en la Generación" : "Generation Error"
                    this.generatingSubMessage = errorMessage
                    this.generatingProgress = 0

                    setTimeout(() => {
                        this.closeGenerationModal()
                        this.$toast.error(this.$store.state._language == "es-ES" ? "Problema al generar información Intercompañías" : "Problem generating Intercompany information")
                    }, 3000)
                })
        },

        // ==================== MÉTODO DE CONSULTA ====================
        f_consultar() {
            if (!this.dialogFechas) {
                this.$toast.warning(this.$store.state._language == "es-ES" ? "Por favor seleccione una vigencia" : "Please select a validity")
                return
            }

            if (this.isGenerating) {
                this.$toast.info(this.$store.state._language == "es-ES" ? "Espere a que termine la generación actual" : "Wait for the current generation to finish")
                return
            }

            this.muestra = false
            this.vigencia = this.dialogFechas
            this.cargarUltimaGeneracion(this.vigencia)
            this.cerrarDetalle()
            this.isEmpty = true
            this.fgl_generado = false
            this.isLoading = true
            this.selectionMode = false
            this.selectedRows = []

            const currentSelections = [...this.selectedRows]

            Balance.getIntercos(this.vigencia, 1)
                .then((result) => {
                    this.intercos = result.data.intercos || []
                    this.fgl_generado = true
                    if (this.intercos.length > 0) {
                        this.filtraPorGrupo()
                        if (currentSelections.length > 0) {
                            this.selectedRows = currentSelections.filter(selected =>
                                this.filteredIntercos.some(item =>
                                    item.identificacion_origen === selected.identificacion_origen &&
                                    item.identificacion_destino === selected.identificacion_destino
                                )
                            )
                            if (this.selectedRows.length > 0) {
                                this.$toast.info(this.$store.state._language == "es-ES" ? `Se restauraron ${this.selectedRows.length} selección(es)` : `Restored ${this.selectedRows.length} selection(s)`)
                            }
                        }
                    } else {
                        this.filteredIntercos = []
                        this.isEmpty = true
                        this.selectedRows = []
                    }
                })
                .catch((error) => {
                    this.error = true
                    this.isEmpty = true
                    this.fgl_generado = true
                    this.intercos = []
                    this.filteredIntercos = []
                    this.selectedRows = []
                    this.$toast.error(this.$store.state._language == "es-ES" ? "Error al consultar los datos" : "Error querying data")
                    console.error("Error en f_consultar:", error)
                })
                .finally(() => {
                    this.isLoading = false
                })
        },

        // ==================== MÉTODOS DE DETALLE ====================
        f_titulo_cuenta() {
            return this.$store.state._language == "es-ES" ? "Detalle de Cuentas Intercompañía" : "Intercompany Account Details"
        },

        f_generar_detalle(p_identificacion_origen, p_identificacion_destino) {
            this.nombreCompaniaOrigenDetalle = this.f_compania(p_identificacion_origen)
            this.nombreCompaniaDestinoDetalle = this.f_compania(p_identificacion_destino)

            this.intercos_detalle = []
            this.fgl_detalle = true
            this.isLoadingDetalle = true

            Balance.getIntercosDetalle(this.vigencia, p_identificacion_origen, p_identificacion_destino, this.dialogReportes)
                .then((result) => {
                    this.intercos_detalle = result.data.intercos_detalle || []
                    this.fgl_diferencia = false
                    this.titulo_cuenta = `${this.nombreCompaniaOrigenDetalle} → ${this.nombreCompaniaDestinoDetalle}`
                })
                .catch((error) => {
                    this.error = true
                    this.$toast.error(this.$store.state._language == "es-ES" ? "Error al cargar el detalle" : "Error loading details")
                    console.error("Error en f_generar_detalle:", error)
                })
                .finally(() => {
                    this.isLoadingDetalle = false
                })
        },

        // ==================== MÉTODOS DE SELECCIÓN ====================
        isSelected(item) {
            return this.selectedRows.some(
                row => row.identificacion_origen === item.identificacion_origen &&
                    row.identificacion_destino === item.identificacion_destino
            )
        },

        toggleRowSelection(r_interco) {
            const existingIndex = this.selectedRows.findIndex(
                row => row.identificacion_origen === r_interco.identificacion_origen &&
                    row.identificacion_destino === r_interco.identificacion_destino
            )

            if (existingIndex > -1) {
                this.selectedRows.splice(existingIndex, 1)
            } else {
                this.selectedRows.push({
                    identificacion_origen: r_interco.identificacion_origen,
                    identificacion_destino: r_interco.identificacion_destino,
                    compania_origen: this.f_compania(r_interco.identificacion_origen),
                    compania_destino: this.f_compania(r_interco.identificacion_destino),
                    saldo_origen: r_interco.saldo_origen,
                    saldo_destino: r_interco.saldo_destino
                })
            }
        },

        toggleSelectionMode() {
            try {
                if (this.selectionMode) {
                    this.selectionMode = false
                    this.selectedRows = []
                    if (this.$toast && this.$toast.info) {
                        this.$toast.info(this.$store.state._language == "es-ES" ? "Modo selección cancelado" : "Selection mode cancelled")
                    }
                } else {
                    this.selectionMode = true
                    if (this.$toast && this.$toast.info) {
                        this.$toast.info(this.$store.state._language == "es-ES" ? "Modo selección activado. Haga clic en las filas para seleccionarlas." : "Selection mode activated. Click on rows to select them.")
                    }
                }
            } catch (err) {
                console.error("Error en toggleSelectionMode:", err)
                this.selectionMode = !this.selectionMode
            }
        },

        onRowSelected(item) {
            if (!item) return
            if (this.selectionMode) {
                this.toggleRowSelection(item)
            } else {
                this.f_generar_detalle(item.identificacion_origen, item.identificacion_destino)
            }
        },

        // ==================== MÉTODOS AUXILIARES ====================
        formato(numero) {
            if (numero === null || numero === undefined) return "0"
            return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(numero)
        },

        f_compania(p_identificacion) {
            if (p_identificacion == undefined || p_identificacion == null) {
                return "Nombre " + p_identificacion
            }
            const v_compañia = this.companias.find((compañia) => compañia.identificacion == p_identificacion)
            if (v_compañia == undefined) {
                return "Nombre " + p_identificacion
            }
            return v_compañia?.nombre
        },

        f_calcularDiferencia(saldo_origen, saldo_destino) {
            const origen = this.toNumber(saldo_origen)
            const destino = this.toNumber(saldo_destino)
            return origen + destino
        },

        toNumber(value) {
            if (!value) return 0
            return Number(value.toString()?.replace(/,/g, ''))
        },

        goBack() {
            this.$router.back()
        },

        f_titulo() {
            const titulo = this.$store.state._language == "es-ES" ? "Saldos Intercompañias" : "INTERCOMPANY ACCOUNTS"
            this.tituloConsolidado = titulo
            return titulo
        },

        // ==================== MÉTODOS DE EXPORTACIÓN EXCEL ====================
        excel(opcion) {
            let datos = {}
            datos.reporte = this.$store.state._language == "es-ES" ? "Saldos Interco" : "Interco Balances"
            datos.vigencia = this.vigencia
            datos.opcion = opcion
            datos.userId = this.$store.state.userId
            datos.userName = this.$store.state.userName

            if (opcion === 1) {
                datos.compania = this.$store.state._language == "es-ES" ? "RESUMIDO" : "SUMMARY"
                datos.titulo = this.f_titulo()
                datos.informacion = this.filteredIntercos
                this.hojaExcelInterco(datos)
            }
            if (opcion === 2) {
                datos.compania = this.$store.state._language == "es-ES" ? "POR CUENTA" : "BY ACCOUNT"
                datos.titulo = this.f_titulo_cuenta()
                datos.informacion = this.intercos_detalle
                this.hojaExcelInterco(datos)
            }
        },

        async hojaExcelInterco(datos) {
            let createdAt1 = new Date()
            let anio = createdAt1.getFullYear()
            let mes = String(createdAt1.getMonth() + 1).padStart(2, '0')
            let dia = String(createdAt1.getDate()).padStart(2, '0')
            let createdAt = anio + "-" + mes + "-" + dia

            const { Workbook } = XLSX
            const workbook = new Workbook()
            const worksheet = workbook.addWorksheet(this.$store.state._language == "es-ES" ? "Intercompañías" : "Intercompany")

            const styles = {
                tituloPrincipal: {
                    font: { name: "Calibri", size: 16, bold: true },
                    alignment: { horizontal: "center", vertical: "middle" },
                    fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFF" } },
                    border: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } }
                },
                subtitulo: {
                    font: { name: "Calibri", size: 11, bold: true },
                    alignment: { horizontal: "center", vertical: "middle" },
                    fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFF" } },
                    border: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } }
                },
                encabezado: {
                    font: { name: "Calibri", size: 11, bold: true, color: { argb: "000000" } },
                    alignment: { horizontal: "center", vertical: "middle" },
                    fill: { type: "pattern", pattern: "solid", fgColor: { argb: "F2F2F2" } },
                    border: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } }
                },
                datoNormal: {
                    font: { name: "Calibri", size: 10 },
                    alignment: { horizontal: "left", vertical: "middle" },
                    border: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } }
                },
                numeroNormal: {
                    font: { name: "Calibri", size: 10 },
                    alignment: { horizontal: "right", vertical: "middle" },
                    numFmt: "#,##0",
                    border: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } }
                }
            }

            let headerColumns = 7
            if (datos.opcion === 2) headerColumns = 8
            if (datos.opcion === 3) headerColumns = 9

            const endCol = String.fromCharCode(64 + headerColumns)

            worksheet.mergeCells(`A1:${endCol}1`)
            worksheet.getCell('A1').value = datos.titulo
            Object.assign(worksheet.getCell('A1'), styles.tituloPrincipal)
            worksheet.getCell('A1').font.color = { argb: "000000" }

            worksheet.mergeCells(`A2:${endCol}2`)
            worksheet.getCell('A2').value = `Vigencia: ${datos.vigencia} | ${datos.compania}`
            Object.assign(worksheet.getCell('A2'), styles.subtitulo)

            worksheet.mergeCells(`A3:${endCol}3`)
            worksheet.getCell('A3').value = `Generado: ${createdAt} por ${datos.userName} | Reporte: ${datos.reporte}`
            Object.assign(worksheet.getCell('A3'), styles.subtitulo)

            worksheet.addRow([])

            let HEADER_ROW = []
            let col_origen = 0
            let col_destino = 0
            let columnWidths = []

            if (datos.opcion === 1) {
                HEADER_ROW = [
                    'NIT ORIGEN',
                    'COMPAÑÍA ORIGEN',
                    'SALDO ORIGEN',
                    'NIT DESTINO',
                    'COMPAÑÍA DESTINO',
                    'SALDO DESTINO',
                    'DIFERENCIA'
                ]
                col_origen = 2
                col_destino = 5
                columnWidths = [{ width: 15 }, { width: 25 }, { width: 18 }, { width: 15 }, { width: 25 }, { width: 18 }, { width: 18 }]
            } else if (datos.opcion === 2) {
                HEADER_ROW = [
                    'NIT ORIGEN',
                    'CUENTA ORIGEN',
                    'DESCRIPCIÓN ORIGEN',
                    'SALDO ORIGEN',
                    'NIT DESTINO',
                    'CUENTA DESTINO',
                    'DESCRIPCIÓN DESTINO',
                    'SALDO DESTINO'
                ]
                col_origen = 3
                col_destino = 7
                columnWidths = [{ width: 15 }, { width: 15 }, { width: 35 }, { width: 18 }, { width: 15 }, { width: 15 }, { width: 35 }, { width: 18 }]
            }

            const headerRow = worksheet.addRow(HEADER_ROW)
            headerRow.eachCell((cell) => {
                Object.assign(cell, styles.encabezado)
            })
            worksheet.columns = columnWidths

            if (datos.opcion === 1) {
                datos.informacion.forEach((fila) => {
                    const v_fila = [
                        fila.identificacion_origen,
                        this.f_compania(fila.identificacion_origen),
                        fila.saldo_origen == null ? null : parseFloat(fila.saldo_origen),
                        fila.identificacion_destino,
                        this.f_compania(fila.identificacion_destino),
                        fila.saldo_destino == null ? null : parseFloat(fila.saldo_destino),
                        this.f_calcularDiferencia(fila.saldo_origen, fila.saldo_destino)
                    ]
                    const currentRow = worksheet.addRow(v_fila)
                    currentRow.eachCell((cell, colNumber) => {
                        if (colNumber === col_origen + 1 || colNumber === col_destino + 1 || colNumber === 7) {
                            Object.assign(cell, styles.numeroNormal)
                        } else {
                            Object.assign(cell, styles.datoNormal)
                        }
                    })
                })
            } else if (datos.opcion === 2) {
                datos.informacion.forEach((fila) => {
                    const v_fila = [
                        fila.identificacion_origen,
                        fila.cuenta_origen,
                        fila.descripcion_origen,
                        fila.saldo_origen == null ? null : parseFloat(fila.saldo_origen),
                        fila.identificacion_destino,
                        fila.cuenta_destino,
                        fila.descripcion_destino,
                        fila.saldo_destino == null ? null : parseFloat(fila.saldo_destino)
                    ]
                    const currentRow = worksheet.addRow(v_fila)
                    currentRow.eachCell((cell, colNumber) => {
                        if (colNumber === col_origen + 1 || colNumber === col_destino + 1) {
                            Object.assign(cell, styles.numeroNormal)
                        } else {
                            Object.assign(cell, styles.datoNormal)
                        }
                    })
                })
            }

            worksheet.views = [{ state: 'frozen', ySplit: 5 }]
            worksheet.autoFilter = { from: { row: 5, column: 1 }, to: { row: 5, column: headerColumns } }

            const file = await workbook.xlsx.writeBuffer()
            const downloadLink = document.createElement("a")

            let fileName = this.$store.state._language == "es-ES" ? "Intercompañías" : "Intercompany"
            if (datos.opcion === 1) fileName = this.$store.state._language == "es-ES" ? "Intercompañías_Resumen" : "Intercompany_Summary"
            if (datos.opcion === 2) fileName = this.$store.state._language == "es-ES" ? "Intercompañías_Detalle" : "Intercompany_Detail"

            downloadLink.download = `${fileName}_${datos.vigencia}_${createdAt}.xlsx`
            downloadLink.href = window.URL.createObjectURL(
                new Blob([file], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
            )
            downloadLink.style.display = "none"
            document.body.appendChild(downloadLink)
            downloadLink.click()
            downloadLink.remove()
        },

        // ==================== EXCEL DE SELECCIÓN ====================
        async excelSelection() {
            if (this.selectedRows.length === 0) {
                this.$toast.warning(this.$store.state._language == "es-ES" ? "No hay filas seleccionadas" : "No rows selected")
                return
            }

            this.isLoading = true
            try {
                const allDetails = []
                for (const row of this.selectedRows) {
                    const result = await Balance.getIntercosDetalle(
                        this.vigencia,
                        row.identificacion_origen,
                        row.identificacion_destino,
                        this.dialogReportes
                    )
                    const detailsWithId = (result.data.intercos_detalle || []).map(detail => ({
                        ...detail,
                        selection_group: `${row.compania_origen} - ${row.compania_destino}`
                    }))
                    allDetails.push(...detailsWithId)
                }

                const datos = {
                    reporte: this.$store.state._language == "es-ES" ? "Saldos Interco - Selección" : "Interco Balances - Selection",
                    vigencia: this.vigencia,
                    opcion: 3,
                    userId: this.$store.state.userId,
                    userName: this.$store.state.userName,
                    compania: this.$store.state._language == "es-ES" ? "DETALLE POR SELECCIÓN" : "DETAIL BY SELECTION",
                    titulo: this.$store.state._language == "es-ES" ? `Detalle de ${this.selectedRows.length} relación(es) seleccionada(s)` : `Detail of ${this.selectedRows.length} selected relation(s)`,
                    informacion: allDetails
                }
                await this.hojaExcelIntercoSeleccion(datos)
                this.$toast.success(this.$store.state._language == "es-ES" ? `Excel generado con ${this.selectedRows.length} relación(es)` : `Excel generated with ${this.selectedRows.length} relation(s)`)
                this.selectionMode = false
                this.selectedRows = []
            } catch (error) {
                console.error("Error en excelSelection:", error)
                this.$toast.error(this.$store.state._language == "es-ES" ? "Error al generar el Excel de selección" : "Error generating selection Excel")
            } finally {
                this.isLoading = false
            }
        },

        async hojaExcelIntercoSeleccion(datos) {
            const { Workbook } = XLSX
            const workbook = new Workbook()
            const worksheet = workbook.addWorksheet(this.$store.state._language == "es-ES" ? "Selección" : "Selection")

            const styles = {
                tituloPrincipal: {
                    font: { name: "Calibri", size: 16, bold: true },
                    alignment: { horizontal: "center", vertical: "middle" },
                    border: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } }
                },
                subtitulo: {
                    font: { name: "Calibri", size: 11, bold: true },
                    alignment: { horizontal: "center", vertical: "middle" },
                    border: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } }
                },
                encabezado: {
                    font: { name: "Calibri", size: 11, bold: true },
                    alignment: { horizontal: "center", vertical: "middle" },
                    fill: { type: "pattern", pattern: "solid", fgColor: { argb: "F2F2F2" } },
                    border: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } }
                },
                datoNormal: {
                    font: { name: "Calibri", size: 10 },
                    alignment: { horizontal: "left", vertical: "middle" },
                    border: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } }
                },
                numeroNormal: {
                    font: { name: "Calibri", size: 10 },
                    alignment: { horizontal: "right", vertical: "middle" },
                    numFmt: "#,##0",
                    border: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } }
                }
            }

            let createdAt1 = new Date()
            let anio = createdAt1.getFullYear()
            let mes = String(createdAt1.getMonth() + 1).padStart(2, '0')
            let dia = String(createdAt1.getDate()).padStart(2, '0')
            let createdAt = anio + "-" + mes + "-" + dia

            worksheet.mergeCells(`A1:I1`)
            worksheet.getCell('A1').value = datos.titulo
            Object.assign(worksheet.getCell('A1'), styles.tituloPrincipal)

            worksheet.mergeCells(`A2:I2`)
            worksheet.getCell('A2').value = `Vigencia: ${datos.vigencia}`
            Object.assign(worksheet.getCell('A2'), styles.subtitulo)

            worksheet.mergeCells(`A3:I3`)
            worksheet.getCell('A3').value = `Generado: ${createdAt} por ${datos.userName} | Reporte: ${datos.reporte}`
            Object.assign(worksheet.getCell('A3'), styles.subtitulo)

            worksheet.addRow([])

            const HEADER_ROW = [
                'NIT ORIGEN', 'CUENTA ORIGEN', 'DESCRIPCIÓN ORIGEN', 'SALDO ORIGEN',
                'NIT DESTINO', 'CUENTA DESTINO', 'DESCRIPCIÓN DESTINO', 'SALDO DESTINO',
                'GRUPO SELECCIÓN'
            ]

            const headerRow = worksheet.addRow(HEADER_ROW)
            headerRow.eachCell((cell) => {
                Object.assign(cell, styles.encabezado)
            })

            worksheet.columns = [
                { width: 15 }, { width: 15 }, { width: 35 }, { width: 18 },
                { width: 15 }, { width: 15 }, { width: 35 }, { width: 18 },
                { width: 35 }
            ]

            let grupoActual = ""
            datos.informacion.forEach((fila) => {
                if (fila.selection_group !== grupoActual) {
                    if (grupoActual !== "") {
                        worksheet.addRow([])
                    }
                    grupoActual = fila.selection_group
                    worksheet.addRow([fila.selection_group])
                    worksheet.mergeCells(`A${worksheet.rowCount}:I${worksheet.rowCount}`)
                    const cell = worksheet.getCell(`A${worksheet.rowCount}`)
                    cell.font = { name: "Calibri", size: 11, bold: true }
                    cell.alignment = { horizontal: "left", vertical: "middle" }
                    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "F2F2F2" } }
                }

                const v_fila = [
                    fila.identificacion_origen,
                    fila.cuenta_origen,
                    fila.descripcion_origen,
                    fila.saldo_origen == null ? null : parseFloat(fila.saldo_origen),
                    fila.identificacion_destino,
                    fila.cuenta_destino,
                    fila.descripcion_destino,
                    fila.saldo_destino == null ? null : parseFloat(fila.saldo_destino),
                    fila.selection_group
                ]

                const currentRow = worksheet.addRow(v_fila)
                currentRow.eachCell((cell, colNumber) => {
                    if (colNumber === 4 || colNumber === 8) {
                        Object.assign(cell, styles.numeroNormal)
                    } else {
                        Object.assign(cell, styles.datoNormal)
                    }
                })
            })

            worksheet.views = [{ state: 'frozen', ySplit: 5 }]
            worksheet.autoFilter = { from: { row: 5, column: 1 }, to: { row: 5, column: 9 } }

            const file = await workbook.xlsx.writeBuffer()
            const downloadLink = document.createElement("a")
            const fileName = this.$store.state._language == "es-ES" ? `Intercompañías_Seleccion_${datos.vigencia}_${createdAt}.xlsx` : `Intercompany_Selection_${datos.vigencia}_${createdAt}.xlsx`
            downloadLink.download = fileName
            downloadLink.href = window.URL.createObjectURL(
                new Blob([file], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
            )
            downloadLink.style.display = "none"
            document.body.appendChild(downloadLink)
            downloadLink.click()
            downloadLink.remove()
        },

        // ==================== EXCEL DE HUÉRFANOS ====================
        async verHuerfanos() {
            if (!this.dialogFechas) {
                this.$toast.warning(this.$store.state._language == "es-ES" ? "Por favor seleccione una vigencia" : "Please select a validity")
                return
            }

            this.isLoading = true
            try {
                const response = await Balance.getIntercosHuerfanos(this.dialogFechas)
                const huerfanos = response.data.huerfanos || response.data[0] || []

                if (!huerfanos || huerfanos.length === 0) {
                    this.$toast.info(this.$store.state._language == "es-ES" ? "No se encontraron registros sin conciliar" : "No unmatched records found")
                    this.isLoading = false
                    return
                }

                const datos = {
                    reporte: this.$store.state._language == "es-ES" ? "Intercos Sin Conciliar" : "Unmatched Intercos",
                    vigencia: this.dialogFechas,
                    opcion: 4,
                    userId: this.$store.state.userId,
                    userName: this.$store.state.userName,
                    compania: this.$store.state._language == "es-ES" ? "SIN CONCILIAR" : "UNMATCHED",
                    titulo: this.$store.state._language == "es-ES" ? "REGISTROS INTERCOMPAÑÍA SIN CONCILIAR" : "UNMATCHED INTERCOMPANY RECORDS",
                    informacion: huerfanos
                }
                await this.hojaExcelIntercosHuerfanos(datos)
                this.$toast.success(this.$store.state._language == "es-ES" ? `Excel generado con ${huerfanos.length} registro(s) sin conciliar` : `Excel generated with ${huerfanos.length} unmatched record(s)`)
            } catch (error) {
                console.error("Error en verHuerfanos:", error)
                this.$toast.error(this.$store.state._language == "es-ES" ? "Error al generar el reporte de registros sin conciliar" : "Error generating unmatched records report")
            } finally {
                this.isLoading = false
            }
        },

        async hojaExcelIntercosHuerfanos(datos) {
            if (!datos.informacion || !Array.isArray(datos.informacion)) {
                console.error("datos.informacion no es un array:", datos.informacion)
                this.$toast.error(this.$store.state._language == "es-ES" ? "Error: Los datos no tienen el formato esperado" : "Error: Data does not have the expected format")
                return
            }

            const { Workbook } = XLSX
            const workbook = new Workbook()
            const worksheet = workbook.addWorksheet(this.$store.state._language == "es-ES" ? "Sin Conciliar" : "Unmatched")

            const styles = {
                tituloPrincipal: {
                    font: { name: "Calibri", size: 16, bold: true },
                    alignment: { horizontal: "center", vertical: "middle" },
                    border: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } }
                },
                subtitulo: {
                    font: { name: "Calibri", size: 11, bold: true },
                    alignment: { horizontal: "center", vertical: "middle" },
                    border: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } }
                },
                encabezado: {
                    font: { name: "Calibri", size: 11, bold: true },
                    alignment: { horizontal: "center", vertical: "middle" },
                    fill: { type: "pattern", pattern: "solid", fgColor: { argb: "F2F2F2" } },
                    border: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } }
                },
                datoNormal: {
                    font: { name: "Calibri", size: 10 },
                    alignment: { horizontal: "left", vertical: "middle" },
                    border: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } }
                },
                numeroNormal: {
                    font: { name: "Calibri", size: 10 },
                    alignment: { horizontal: "right", vertical: "middle" },
                    numFmt: "#,##0",
                    border: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } }
                }
            }

            let createdAt1 = new Date()
            let anio = createdAt1.getFullYear()
            let mes = String(createdAt1.getMonth() + 1).padStart(2, '0')
            let dia = String(createdAt1.getDate()).padStart(2, '0')
            let createdAt = anio + "-" + mes + "-" + dia

            worksheet.mergeCells(`A1:J1`)
            worksheet.getCell('A1').value = datos.titulo
            Object.assign(worksheet.getCell('A1'), styles.tituloPrincipal)

            worksheet.mergeCells(`A2:J2`)
            worksheet.getCell('A2').value = `Vigencia: ${datos.vigencia}`
            Object.assign(worksheet.getCell('A2'), styles.subtitulo)

            worksheet.mergeCells(`A3:J3`)
            worksheet.getCell('A3').value = `Generado: ${createdAt} por ${datos.userName} | Reporte: ${datos.reporte}`
            Object.assign(worksheet.getCell('A3'), styles.subtitulo)

            worksheet.addRow([])

            const HEADER_ROW = [
                'Vigencia', 'Secuencia', 'NIT Compañía', 'Compañía',
                'Cuenta', 'Descripción', 'Saldo Actual', 'Ajuste Actual',
                'NIT Contraparte', 'Compañía Contraparte'
            ]

            const headerRow = worksheet.addRow(HEADER_ROW)
            headerRow.eachCell((cell) => {
                Object.assign(cell, styles.encabezado)
            })

            worksheet.columns = [
                { width: 12 }, { width: 12 }, { width: 15 }, { width: 35 },
                { width: 15 }, { width: 45 }, { width: 18 }, { width: 18 },
                { width: 15 }, { width: 35 }
            ]

            datos.informacion.forEach((fila) => {
                const v_fila = [
                    fila.vigencia,
                    fila.secuencia,
                    fila.identificacion_compania,
                    fila.nombre_compania,
                    fila.cuenta,
                    fila.descripcion,
                    fila.saldo_actual == null ? 0 : parseFloat(fila.saldo_actual),
                    fila.ajuste_actual == null ? 0 : parseFloat(fila.ajuste_actual),
                    fila.identificacion_contraparte || '',
                    fila.nombre_contraparte || ''
                ]
                const currentRow = worksheet.addRow(v_fila)
                currentRow.eachCell((cell, colNumber) => {
                    if (colNumber === 7 || colNumber === 8) {
                        Object.assign(cell, styles.numeroNormal)
                    } else {
                        Object.assign(cell, styles.datoNormal)
                    }
                })
            })

            worksheet.views = [{ state: 'frozen', ySplit: 5 }]
            worksheet.autoFilter = { from: { row: 5, column: 1 }, to: { row: 5, column: 10 } }

            const file = await workbook.xlsx.writeBuffer()
            const downloadLink = document.createElement("a")
            downloadLink.download = `Sin_Conciliar_${datos.vigencia}_${createdAt}.xlsx`
            downloadLink.href = window.URL.createObjectURL(
                new Blob([file], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
            )
            downloadLink.style.display = "none"
            document.body.appendChild(downloadLink)
            downloadLink.click()
            downloadLink.remove()
        },

        // ==================== EXCEL DE DIFERENCIAS ====================
        async verDiferenciasSecuencia() {
            if (!this.dialogFechas) {
                this.$toast.warning(this.$store.state._language == "es-ES" ? "Por favor seleccione una vigencia" : "Please select a validity")
                return
            }

            this.isLoading = true
            try {
                const response = await Balance.getIntercosDiferencias(this.dialogFechas)
                const diferencias = response.data.diferencias || response.data[0] || []

                if (!diferencias || diferencias.length === 0) {
                    this.$toast.info(this.$store.state._language == "es-ES" ? "No se encontraron diferencias de secuencias" : "No sequence differences found")
                    this.isLoading = false
                    return
                }

                const datos = {
                    reporte: this.$store.state._language == "es-ES" ? "Intercos Diferencias de Secuencias" : "Intercos Sequence Differences",
                    vigencia: this.dialogFechas,
                    opcion: 5,
                    userId: this.$store.state.userId,
                    userName: this.$store.state.userName,
                    compania: this.$store.state._language == "es-ES" ? "DIFERENCIAS" : "DIFFERENCES",
                    titulo: this.$store.state._language == "es-ES" ? "DIFERENCIAS ENTRE SECUENCIAS INTERCOMPAÑÍA" : "INTERCOMPANY SEQUENCE DIFFERENCES",
                    informacion: diferencias
                }
                await this.hojaExcelIntercosDiferencias(datos)
                this.$toast.success(this.$store.state._language == "es-ES" ? "Excel generado con diferencias de secuencias" : "Excel generated with sequence differences")
            } catch (error) {
                console.error("Error en verDiferenciasSecuencia:", error)
                this.$toast.error(this.$store.state._language == "es-ES" ? "Error al generar el reporte de diferencias de secuencias" : "Error generating sequence differences report")
            } finally {
                this.isLoading = false
            }
        },

        async hojaExcelIntercosDiferencias(datos) {
            if (!datos.informacion || !Array.isArray(datos.informacion)) {
                console.error("datos.informacion no es un array:", datos.informacion)
                this.$toast.error(this.$store.state._language == "es-ES" ? "Error: Los datos no tienen el formato esperado" : "Error: Data does not have the expected format")
                return
            }

            const { Workbook } = XLSX
            const workbook = new Workbook()
            const worksheet = workbook.addWorksheet(this.$store.state._language == "es-ES" ? "Diferencias Sec." : "Seq. Differences")

            const styles = {
                tituloPrincipal: {
                    font: { name: "Calibri", size: 16, bold: true },
                    alignment: { horizontal: "center", vertical: "middle" },
                    border: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } }
                },
                subtitulo: {
                    font: { name: "Calibri", size: 11, bold: true },
                    alignment: { horizontal: "center", vertical: "middle" },
                    border: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } }
                },
                encabezado: {
                    font: { name: "Calibri", size: 11, bold: true },
                    alignment: { horizontal: "center", vertical: "middle" },
                    fill: { type: "pattern", pattern: "solid", fgColor: { argb: "F2F2F2" } },
                    border: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } }
                },
                numeroNormal: {
                    font: { name: "Calibri", size: 10 },
                    alignment: { horizontal: "right", vertical: "middle" },
                    numFmt: "#,##0",
                    border: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } }
                },
                datoNormal: {
                    font: { name: "Calibri", size: 10 },
                    alignment: { horizontal: "left", vertical: "middle" },
                    border: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } }
                }
            }

            let createdAt1 = new Date()
            let anio = createdAt1.getFullYear()
            let mes = String(createdAt1.getMonth() + 1).padStart(2, '0')
            let dia = String(createdAt1.getDate()).padStart(2, '0')
            let createdAt = anio + "-" + mes + "-" + dia

            worksheet.mergeCells(`A1:E1`)
            worksheet.getCell('A1').value = datos.titulo
            Object.assign(worksheet.getCell('A1'), styles.tituloPrincipal)

            worksheet.mergeCells(`A2:E2`)
            worksheet.getCell('A2').value = `Vigencia: ${datos.vigencia}`
            Object.assign(worksheet.getCell('A2'), styles.subtitulo)

            worksheet.mergeCells(`A3:E3`)
            worksheet.getCell('A3').value = `Generado: ${createdAt} por ${datos.userName} | Reporte: ${datos.reporte}`
            Object.assign(worksheet.getCell('A3'), styles.subtitulo)

            worksheet.addRow([])

            const HEADER_ROW = [
                'Vigencia', 'Secuencia', 'Saldo Origen', 'Saldo Destino', 'Diferencia'
            ]

            const headerRow = worksheet.addRow(HEADER_ROW)
            headerRow.eachCell((cell) => {
                Object.assign(cell, styles.encabezado)
            })

            worksheet.columns = [
                { width: 12 }, { width: 14 }, { width: 18 }, { width: 18 }, { width: 18 }
            ]

            datos.informacion.forEach((fila) => {
                const v_fila = [
                    fila.vigencia,
                    fila.secuencia,
                    fila.saldo_origen == null ? 0 : parseFloat(fila.saldo_origen),
                    fila.saldo_destino == null ? 0 : parseFloat(fila.saldo_destino),
                    fila.diferencia == null ? 0 : parseFloat(fila.diferencia)
                ]
                const currentRow = worksheet.addRow(v_fila)
                currentRow.eachCell((cell, colNumber) => {
                    if (colNumber >= 3) {
                        Object.assign(cell, styles.numeroNormal)
                    } else {
                        Object.assign(cell, styles.datoNormal)
                    }
                })
            })

            worksheet.views = [{ state: 'frozen', ySplit: 5 }]
            worksheet.autoFilter = { from: { row: 5, column: 1 }, to: { row: 5, column: 5 } }

            const file = await workbook.xlsx.writeBuffer()
            const downloadLink = document.createElement("a")
            downloadLink.download = `Intercos_Diferencias_Secuencias_${datos.vigencia}_${createdAt}.xlsx`
            downloadLink.href = window.URL.createObjectURL(
                new Blob([file], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
            )
            downloadLink.style.display = "none"
            document.body.appendChild(downloadLink)
            downloadLink.click()
            downloadLink.remove()
        }
    },
    mounted() {
        this.isLoading = true

        Grupo.getAllGrupos()
            .then((result) => {
                this.listGrupos = result.data.grupos || []
            })
            .catch((error) => {
                console.error("Error al cargar los grupos:", error)
                this.$toast.error(this.$store.state._language == "es-ES" ? "Error al cargar los grupos" : "Error loading groups")
            })

        Balance.getFechas()
            .then((result) => {
                this.fechas = result.data.fechas || []
                this.dialogFechas = ''
            })
            .catch(() => {
                this.error = true
                this.$toast.error(this.$store.state._language == "es-ES" ? "Error al cargar las vigencias" : "Error loading validities")
            })

        Balance.getCompanias(true)
            .then((result) => {
                this.companias = result.data.companias || []
            })
            .catch(() => {
                this.error = true
                this.$toast.error(this.$store.state._language == "es-ES" ? "Error al cargar las compañías" : "Error loading companies")
            })

        Balance.getReportes()
            .then((result) => {
                this.reportes = result.data.reportes || []
            })
            .catch(() => {
                this.error = true
                this.$toast.error(this.$store.state._language == "es-ES" ? "Error al cargar los reportes" : "Error loading reports")
            })

        this.isEmpty = true
        this.intercos = []
        this.filteredIntercos = []
        this.isLoading = false
        this.f_titulo()
    }
}
</script>

<style src="../main.css"></style>

<style scoped>
/* ========== ESTILOS DE FILTROS PRINCIPALES ========== */
.niif-header .filters-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: 0 !important;
}

.niif-header .filter-select {
    min-width: 200px;
    max-width: 280px;
    width: auto;
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
}

.niif-header .filter-select option {
    white-space: normal;
    word-break: break-word;
    padding: 4px 8px;
}

/* ========== BOTÓN CONSULTAR ========== */
.niif-header .consult-btn {
    height: 38px;
    min-height: 38px;
    padding: 0 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 14px;
    background-color: #6c757d !important;
    color: white !important;
    border: 1px solid #6c757d !important;
    cursor: pointer;
    flex-shrink: 0;
}

.niif-header .consult-btn:hover:not(:disabled) {
    background-color: #5a6268 !important;
    border-color: #545b62 !important;
}

.niif-header .consult-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

/* ========== BOTONES DE TOOLBAR ========== */
.table-toolbar-btn {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
    background-color: #1f4f83 !important;
    color: white !important;
    border: 1px solid #1f4f83 !important;
}

.table-toolbar-btn:hover:not(:disabled) {
    background-color: #1a446f !important;
    border-color: #1a446f !important;
}

.table-toolbar-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

/* ========== DROPDOWN DE EXPORTAR ========== */
.table-toolbar-dropdown {
    display: inline-block;
}

.table-toolbar-dropdown-btn {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
    background-color: #1f4f83 !important;
    color: white !important;
    border: 1px solid #1f4f83 !important;
    cursor: pointer;
    flex-shrink: 0;
}

.table-toolbar-dropdown-btn:hover:not(:disabled) {
    background-color: #1a446f !important;
    border-color: #1a446f !important;
}

.table-toolbar-dropdown-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.table-toolbar-dropdown .dropdown-item:active {
    background-color: #1f4f83 !important;
    color: white !important;
}

/* ========== BOTONES DE ACCIÓN EN TABLA ========== */
.table-action-btn {
    border: none;
    border-radius: 4px;
    transition: all 0.2s;
}

.table-action-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* ========== ESTILOS DE FLECHA Y TÍTULO ========== */
.page-back-btn {
    padding: 0;
    font-size: 1.5rem;
    line-height: 1;
}

.back-icon {
    font-size: 1.75rem !important;
    width: 1.75rem !important;
    height: 1.75rem !important;
}

.param-title {
    font-size: 2rem !important;
    font-weight: 500;
}

/* ========== INFO DE GENERACIÓN ========== */
.generation-info {
    font-size: 13px;
    color: #6c757d;
    padding: 4px 8px;
    background: #f8f9fa;
    border-radius: 4px;
    border: 1px solid #e9ecef;
}

/* ========== VISTA DE DETALLE ========== */
.detail-view-container {
    animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
}

/* ========== RESPONSIVE ========== */
@media (max-width: 1200px) {
    .niif-header .filter-select {
        min-width: 160px;
        max-width: 220px;
        height: 36px;
        font-size: 13px;
    }
    .niif-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
    .table-toolbar-btn,
    .table-toolbar-dropdown-btn {
        height: 28px;
        min-height: 28px;
        font-size: 12px;
        padding: 0 12px;
    }
}

@media (max-width: 992px) {
    .niif-header .filters-container {
        flex-wrap: wrap;
        width: 100%;
        margin-top: 8px;
        margin-left: 0 !important;
        justify-content: flex-start;
        gap: 6px;
    }
    .niif-header .filter-select {
        min-width: 150px;
        max-width: 200px;
        height: 36px;
        font-size: 13px;
        flex: 1 1 auto;
    }
    .niif-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
    .niif-toolbar-left {
        flex-wrap: wrap;
    }
    .niif-toolbar-right {
        flex-wrap: wrap;
    }
}

@media (max-width: 576px) {
    .param-title {
        font-size: 1.5rem !important;
    }
    .niif-header .filter-select {
        min-width: 100%;
        max-width: 100%;
        height: 36px;
        font-size: 13px;
        flex: 0 0 100%;
    }
    .niif-header .filters-container {
        gap: 4px;
    }
    .niif-header .consult-btn {
        width: 100%;
        flex: 0 0 100%;
        height: 36px;
        margin-left: 0 !important;
        margin-top: 4px;
        font-size: 13px;
        padding: 0 16px;
    }
    .niif-toolbar-left {
        width: 100%;
        flex-wrap: wrap;
    }
    .niif-toolbar-right {
        width: 100%;
        flex-wrap: wrap;
        margin-top: 4px;
    }
    .table-toolbar-btn,
    .table-toolbar-dropdown-btn {
        font-size: 11px;
        padding: 0 8px;
        height: 28px;
        min-height: 28px;
        flex: 1;
    }
    .table-toolbar-dropdown {
        width: 100%;
    }
    .table-toolbar-dropdown .dropdown-menu {
        width: 100%;
    }
}

.admin-table-block :deep(table thead th.text-center) {
    text-align: center !important;
}

.admin-table-block :deep(table tbody td.text-right),
.admin-table-block :deep(table tbody td.text-right > *) {
    text-align: right !important;
    justify-content: flex-end;
}

/* Monospace también a la derecha */
.admin-table-block :deep(table tbody td.text-right .font-monospace) {
    text-align: right !important;
    width: 100%;
    display: inline-block;
}

/* ========== FILAS SELECCIONADAS ========== */
.admin-table-block :deep(tr.table-selected),
.admin-table-block :deep(tr.table-selected > td) {
    background-color: #e3f0ff !important;
}

/* cursor de mano solo en modo selección */
.admin-table-block :deep(tbody tr) {
    cursor: pointer;
}

/* ========== UNIFICAR TIPOGRAFÍA DE TODA LA TABLA ========== */
.admin-table-block :deep(table),
.admin-table-block :deep(table thead th),
.admin-table-block :deep(table tbody td),
.admin-table-block :deep(table tbody td *) {
    font-family: inherit !important;
    font-size: 13px !important;
    font-weight: 400 !important;
    letter-spacing: normal !important;
}

/* Excepción: mantener el encabezado en negrita */
.admin-table-block :deep(table thead th) {
    font-weight: 600 !important;
}

/* Quitar el monospace de los valores numéricos */
.admin-table-block :deep(table tbody td .font-monospace) {
    font-family: inherit !important;
    font-size: 13px !important;
    font-weight: 400 !important;
}
</style>